import { apiFetch } from '@/lib/apiClient';
import type { DiscountRequest, DiscountRequestStatus } from '@coreltb/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const discountKeys = {
  all: ['discount-requests'] as const,
  list: (status?: DiscountRequestStatus) => [...discountKeys.all, 'list', status] as const,
  byOffer: (offerId: string) => [...discountKeys.all, 'by-offer', offerId] as const,
};

export interface CreateDiscountRequestInput {
  offerId: string;
  requestedPercent: number;
  reason: string;
}

/**
 * POST — handlowiec prosi o niestandardowy rabat dla oferty.
 * Backend zwraca 409 jezeli juz istnieje pending dla tej oferty.
 */
export function useCreateDiscountRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDiscountRequestInput) =>
      apiFetch<{ id: string; status: 'pending' }>('/api/discount-requests', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: discountKeys.all }),
  });
}

/**
 * Lista request'ow widziana przez admina/dyrektora.
 *
 * Auto-refresh co 30s zeby badge w sidebarze (PendingDiscountBadge)
 * pokazywal swieza liczbe pendingowych prosb bez konieczności F5.
 */
export function useDiscountRequestsList(status?: DiscountRequestStatus) {
  return useQuery({
    queryKey: discountKeys.list(status),
    queryFn: () => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return apiFetch<DiscountRequest[]>(`/api/discount-requests${qs}`);
    },
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/**
 * Request'y dla konkretnej oferty — handlowiec sprawdza status swojej prosby.
 *
 * Polling 5s zamiast 30s: gdy admin akceptuje rabat, handlowiec dostaje
 * info w max 5s zamiast 30s+. Endpoint ten jest enabled TYLKO gdy
 * handlowiec siedzi na ofercie z pending request (mountowany komponent
 * DiscountRequestPanel) — wiec nie generuje stalego ruchu.
 *
 * Przy 5 rownoczesnych pendingach 0.17% extra Workers usage (negligible).
 */
export function useDiscountRequestsByOffer(offerId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: discountKeys.byOffer(offerId ?? ''),
    queryFn: async () => {
      const data = await apiFetch<
        Array<{
          id: string;
          offerId: string;
          requestedPercent: number;
          reason: string;
          status: DiscountRequestStatus;
          createdBy: string;
          reviewedBy: string | null;
          reviewComment: string | null;
          createdAt: number;
          reviewedAt: number | null;
        }>
      >(`/api/discount-requests/by-offer/${offerId}`);
      // Auto-invalidate offer cache gdy ktorykolwiek request zmienil status
      // z pending → approved/rejected (admin podjal decyzje). Powoduje to
      // refetch useOffer ktory pokaze nowy discount_percent w UI bez czekania
      // na nastepny tick globalnego pollingu.
      if (offerId && data.some((r) => r.status === 'approved' || r.status === 'rejected')) {
        qc.invalidateQueries({ queryKey: ['offers', 'detail', offerId] });
      }
      return data;
    },
    enabled: Boolean(offerId),
    staleTime: 5 * 1000,
    refetchInterval: 5 * 1000,
  });
}

export interface ReviewDiscountRequestInput {
  id: string;
  status: 'approved' | 'rejected';
  reviewComment?: string;
}

export function useReviewDiscountRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewDiscountRequestInput) =>
      apiFetch<{ id: string; status: DiscountRequestStatus }>(
        `/api/discount-requests/${input.id}`,
        {
          method: 'PATCH',
          body: { status: input.status, reviewComment: input.reviewComment },
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: discountKeys.all }),
  });
}
